import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import Signup from './views/Signup';
import Login from './views/Signin';
import MainApp from './MainApp';
import Onboarding from './components/Onboarding/Onboarding';
import ProtectedRoute from './routes/ProtectedRoute';
import PublicRoute from './routes/PublicRoute';
import { Toaster } from 'react-hot-toast';
import {
  fetchMeThunk,
  logoutThunk,
  rehydrateAuth,
} from './redux/slices/authSlice';
import { fetchUserStatistics } from './redux/slices/userStatisticsSlice';
import { useEffect, useState } from 'react';
import ForgotPassword from './views/ForgotPassword';
import Loader from './components/Loader/Loader';
import { useLocation } from 'react-router-dom';
import PaymentCancel from './views/PaymentCancel';
import PaymentSuccess from './views/PaymentSuccess';
import NoRedirectRoute from './routes/NoRedirectRoute';
import FeedbackPage from './components/layout/FeedbackPage';
import FloatingFeedbackButton from './components/layout/FloatingFeedbackButton';
import SharedChatView from './views/SharedChatView';

function FloatingFeedbackButtonGate({ isDarkMode }) {
  const location = useLocation();
  const hideOnPublicShare = location.pathname.startsWith('/share/');

  if (hideOnPublicShare) return null;

  return <FloatingFeedbackButton isDarkMode={isDarkMode} />;
}

function RootRedirect({ isAuthenticated }) {
  const location = useLocation();

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const price = params.get('package');

    // ✅ Capture upgrade intent ONCE, before redirect wipes the URL
    if (price && !sessionStorage.getItem('upgrade_intent')) {
      sessionStorage.setItem('upgrade_intent', price);
      sessionStorage.setItem('from_upgrade_intent', 'true');
    }
  }, [location.search]);

  return <Navigate to={isAuthenticated ? '/app' : '/login'} replace />;
}

export default function App() {
  const dispatch = useDispatch();
  const { isAuthenticated, token } = useSelector((state) => state.auth);
  const [loading, setLoading] = useState(true);
  const [isDarkMode, setIsDarkMode] = useState(() => {
    const savedTheme = localStorage.getItem('theme');
    return savedTheme === 'dark';
  });

  const toggleTheme = () => {
    const newTheme = !isDarkMode;
    setIsDarkMode(newTheme);
    localStorage.setItem('theme', newTheme ? 'dark' : 'light');
  };

  useEffect(() => {
    dispatch(rehydrateAuth());

    if (token) {
      dispatch(fetchMeThunk())
        .unwrap()
        .then((userData) => {
          if (userData?.id || userData?.uid) {
            dispatch(fetchUserStatistics(userData.id || userData.uid));
          }
        })
        .catch(() => {
          dispatch(logoutThunk());
        })
        .finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, [dispatch, token]);

  if (loading)
    return (
      <>
        <Loader isDarkMode={isDarkMode} />
      </>
    );

  return (
    <Router>
      <Routes>
        <Route
          path="/"
          element={<RootRedirect isAuthenticated={isAuthenticated} />}
        />
        <Route
          path="/app/payment-success"
          element={
            <NoRedirectRoute>
              <PaymentSuccess />
            </NoRedirectRoute>
          }
        />
        <Route
          path="/app/payment-cancel"
          element={
            <NoRedirectRoute>
              <PaymentCancel />
            </NoRedirectRoute>
          }
        />
        <Route
          path="/app/feedback"
          element={
            <>
              <FeedbackPage isDarkMode={isDarkMode} />
            </>
          }
        />
        <Route
          path="/share/:token"
          element={<SharedChatView isDarkMode={isDarkMode} />}
        />
        <Route
          path="/login"
          element={
            <PublicRoute>
              <Login />
            </PublicRoute>
          }
        />

        <Route
          path="/signup"
          element={
            <PublicRoute>
              <Signup />
            </PublicRoute>
          }
        />
        <Route
          path="/forgot-password"
          element={
            <PublicRoute>
              <ForgotPassword />
            </PublicRoute>
          }
        />
        <Route
          path="/onboarding"
          element={
            <ProtectedRoute>
              <Onboarding />
            </ProtectedRoute>
          }
        />
        <Route
          path="/app/*"
          element={
            <ProtectedRoute>
              <MainApp isDarkMode={isDarkMode} toggleTheme={toggleTheme} />
            </ProtectedRoute>
          }
        />
        <Route path="*" element={<Navigate to="/" />} />
      </Routes>

      <Toaster
        position="top-right"
        gutter={12}
        toastOptions={{
          duration: 3500,
          style: {
            padding: '12px 16px',
            borderRadius: '0.5rem',
            fontSize: '0.875rem',
            fontWeight: 500,
            backgroundColor: isDarkMode ? '#1e293b' : '#ffffff',
            color: isDarkMode ? '#f8fafc' : '#0f172a',
            border: isDarkMode ? '1px solid #334155' : '1px solid #e2e8f0',
            boxShadow: isDarkMode
              ? '0 4px 20px rgba(0, 0, 0, 0.45)'
              : '0 4px 20px rgba(0, 0, 0, 0.08)',
            backdropFilter: 'blur(6px)',
            WebkitBackdropFilter: 'blur(6px)',
            transition: 'all 0.2s ease-in-out',
          },

          iconTheme: {
            primary: isDarkMode ? '#818cf8' : '#4f46e5',
            secondary: isDarkMode ? '#1e293b' : '#ffffff',
          },

          success: {
            style: {
              backgroundColor: isDarkMode ? '#14532d' : '#dcfce7',
              color: isDarkMode ? '#bbf7d0' : '#166534',
              border: isDarkMode ? '1px solid #15803d' : '1px solid #86efac',
            },
            iconTheme: {
              primary: isDarkMode ? '#22c55e' : '#15803d',
              secondary: isDarkMode ? '#14532d' : '#ffffff',
            },
          },
          error: {
            style: {
              backgroundColor: isDarkMode ? '#450a0a' : '#fee2e2',
              color: isDarkMode ? '#fecaca' : '#991b1b',
              border: isDarkMode ? '1px solid #b91c1c' : '1px solid #fecaca',
            },
            iconTheme: {
              primary: isDarkMode ? '#ef4444' : '#b91c1c',
              secondary: isDarkMode ? '#450a0a' : '#ffffff',
            },
          },
          loading: {
            style: {
              backgroundColor: isDarkMode ? '#0f172a' : '#f1f5f9',
              color: isDarkMode ? '#e2e8f0' : '#0f172a',
            },
          },
        }}
      />
      <FloatingFeedbackButtonGate isDarkMode={isDarkMode} />
    </Router>
  );
}
